import React, {useEffect, useState} from "react";
import {useUrlState, useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {Card} from "reactstrap";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {VsiBubble, VsiH4, VsiMedLabel} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import Grid from "../../../components/Grid";
import {VsiSelectLabel} from "../../../styled/VsiLabeledSelect.styled";
import CustomDateInput from "../../../components/CustomDateInput";
import ReactDatePicker from "react-datepicker";
import {dateFromPeriod, periodFromDate} from "../../../utils/Helpers";
import {clusterDeltaReportDefs, vmsDeltaReportDefs} from "../../../utils/gridColumnDefs/virtual/deltaReport";
import {growthFormatter, intGrowthFormatter} from "../../../utils/gridFormatters";
import {useNavigate} from "react-router-dom";

const commonRollupConfigs = [
    {
        label: "vCPUs",
        field: "vcpus_growth",
        formatter: intGrowthFormatter
    },
    {
        label: "Memory",
        field: "memory_growth",
        formatter: growthFormatter
    },
    {
        label: "Disk Allocated",
        field: "disk_alloc_growth",
        formatter: growthFormatter
    }
];

const dcsAndClusterConfigs = [
    {
        label: "VMs",
        field: "vms_growth",
        formatter: intGrowthFormatter
    },
    {
        label: "Hosts",
        field: "num_hosts_growth",
        formatter: intGrowthFormatter
    },
    ...commonRollupConfigs
];

const allOptions = [
    {
        value: "data_centers",
        label: "Data Centers",
        rollup: dcsAndClusterConfigs
    },
    // {
    //   value: "vcenters",
    //   label: "vCenters",
    //   rollup: dcsAndClusterConfigs
    // },
    {
        value: "clusters",
        label: "Clusters",
        rollup: dcsAndClusterConfigs
    },
    {
        value: "vms",
        label: "VMs",
        // doesn't need num vms
        rollup: commonRollupConfigs
    }
];

// cluster and data centers use the same defs
const defsMap = {
    data_centers: [...clusterDeltaReportDefs],
    vcenters: [{
        "field": "vcenter_name",
        "headerName": "vCenter"
    }, ...clusterDeltaReportDefs],
    clusters: [{
        "field": "cluster_name",
        "headerName": "Cluster"
    }, ...clusterDeltaReportDefs],
    vms: vmsDeltaReportDefs
};

export default function DeltaReport({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);

    const {theme, client, periods, periodId} = useVsiAppContext();

    const [params, setParams] = useUrlState({
        report_type: allOptions[0].value,
    });

    const {
        report_type: reportType,
    } = params;

    const selectedReportType = allOptions.find(opt => opt.value === reportType) ?? {};
    const [colDefs, setColDefs] = useState(defsMap[reportType] || defsMap[allOptions[0].value]);
    const [dateList, setDateList] = useState(periods?.map?.(dateObj => {
        let date = new Date(dateObj?.collection_date);
        // Compensate for timezone differences
        date = new Date(date.getTime() - date.getTimezoneOffset() * -60000);
        return date;
    }));
    const [gridData, setGridData] = useState([]);
    const [gridRollup, setGridRollup] = useState({});

    const [startPeriod, setStartPeriod] = useState(periods.filter(p => p.period_id >= periodId - 32).sort((a, b) => a.period_id - b.period_id)[0].period_id);
    const [endPeriod, setEndPeriod] = useState(periodId);

    const [{recordset}, {isLoading}] = useDataWithRoute(`/api/v6/report/virtual_${reportType}_delta_report_proc?client_id=${client.clientId}&beginning_period_id=${startPeriod}&end_period_id=${endPeriod}`);

    const onReportTypeChange = type => {
        setParams({
            report_type: type.value
        });
    };

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) {
                rowData.push(node.data);
            }
        });
        setGridData(rowData);
    };

    useEffect(() => {
        setColDefs(defsMap[reportType]);
    }, [reportType]);


    useEffect(() => {
        setDateList(periods?.map?.(dateObj => {
            let date = new Date(dateObj?.collection_date);
            // Compensate for timezone differences
            date = new Date(date.getTime() - date.getTimezoneOffset() * -60000);
            return date;
        }));

    }, [periods]);

    useEffect(() => {
        setGridData([...(recordset || [])]);
    }, [recordset, colDefs]);

    useEffect(() => {
        const newRollup = {};
        gridData.forEach(datum => {
            (colDefs || []).forEach(rollupConfig => {
                const {field} = rollupConfig;
                if (!newRollup.hasOwnProperty(field)) {
                    newRollup[field] = 0;
                }
                newRollup[field] = newRollup[field] + datum[field];
            });
        });
        setGridRollup(newRollup);
    }, [gridData, colDefs]);
    let navigate = useNavigate();
    const onRowClick = (e) => {
        switch (reportType) {
            case 'vms':
                navigate(`/reports/virtual/vm/vm-summary?vm_name=${encodeURI(e.data.vm_name)}&vcenter_name=${encodeURI(e.data.vcenter_name)}&cluster_name=${encodeURI(e.data.cluster_name)}&esx_host_name=${encodeURI(e.data.esx_host_name)}&period_id=${periodId}`)
                break;
            case 'clusters':
                navigate(`/reports/virtual/cluster/cluster-summary?cluster_name=${encodeURI(e.data.cluster_name)}&vcenter_name=${encodeURI(e.data.device_name)}&period_id=${periodId}`);
                break;
            case 'data_centers':
                navigate(`/reports/virtual/vcenter/data-center-summary?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURI(e.data.device_name)}&data_center_name=${encodeURI(e.data.datacenter_name)}`);
                break;
            default:
                break;
        }
    };

    return (

        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Delta Report </VsiH4>
            <VsiBubble noAutobot>
                <div className={"d-flex flex-row justify-content-between"}>
                    <div className={"d-flex flex-row"}>
                        <VsiLabeledSelect isDisabled={isLoading}
                                          isLoading={isLoading}
                                          options={allOptions}
                                          label={"Select Unit"}
                                          onChange={onReportTypeChange}
                                          defaultValue={selectedReportType}/>
                        {/*<VsiLabeledSelect isDisabled={isLoading}*/}
                        {/*                  isLoading={isLoading}*/}
                        {/*                  options={dateDropdownOptions}*/}
                        {/*                  label={"Select Increment"}*/}
                        {/*                  onChange={onIncrementTypeChange}*/}
                        {/*                  defaultValue={dateDropdownOptions.find(opt => parseInt(opt.increment) === parseInt(increment))}/>*/}
                        <div className={"ms-2"}>
                            <VsiSelectLabel>
                                Start
                            </VsiSelectLabel>
                            <ReactDatePicker
                                includeDates={dateList}
                                selected={dateFromPeriod(startPeriod)}
                                onChange={(date) => setStartPeriod(periodFromDate(date))}
                                customInput={<CustomDateInput/>}
                            />
                        </div>
                        <div>
                            <VsiSelectLabel>
                                End
                            </VsiSelectLabel>
                            <ReactDatePicker
                                includeDates={dateList}
                                selected={dateFromPeriod(endPeriod)}
                                onChange={(date) => setEndPeriod(periodFromDate(date))}
                                customInput={<CustomDateInput/>}
                            />
                        </div>
                    </div>
                    <div className={"d-flex flex-row justify-content-between"}>
                        <div className={"d-flex flex-row justify-content-end me-2"}>
                            {(selectedReportType?.rollup || []).map((config, index) => {
                                const {field, formatter, label} = config;
                                const isSuffix = label?.toLowerCase?.()?.includes?.("disk") || label?.toLowerCase?.()?.includes?.("memory");
                                const labelSuffix = reportType === "vms" ? "GiB" : "TiB";
                                const val = gridRollup?.[field];
                                return (
                                    <div key={`rollup_${field}`}
                                         className={"d-flex flex-column align-items-end justify-content-end px-2"}>
                                        <VsiMedLabel>{`${label}${isSuffix ? ` (${labelSuffix})` : ""}`}</VsiMedLabel>
                                        <VsiH4 style={{marginBottom: 0}}>
                                            {typeof val === "number" && !isLoading ? formatter({
                                                value: isSuffix && reportType !== "vms" ? val / 1024 : val
                                            }) : "-"}
                                        </VsiH4>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </VsiBubble>
            <VsiBubble noAutobot>
                <Grid data={recordset}
                      filterChanged={filterChanged}
                      columnDefs={colDefs}
                      rowDoubleClicked={onRowClick}
                      saveParams={{
                          report_type: reportType
                      }}/>
            </VsiBubble>
        </Card>
    );
}
